import { useTranslation } from "react-i18next";
import { PARAMETER_OPTIONS, RPE_RANGE } from "../../../constants";
import convertToRiR from "../../../utils/converters/convert-intensity";
import ParameterInput from "../../inputs/parameter-input";
import TimeSelector from "../../inputs/time-selector";
import UnitInput from "../../inputs/unit-input-add-exercise";
import { mapParameter } from "../../other-exercise/CustomParametersSets";
import LoadToggler from "./LoadToggler";
import ModTooltip from "./ModTooltip";
import { getEnterAction } from "../../../utils/add-exercise-buttons";
import SvgIcon from "../../SvgIcon";
import { handleSetEntryKeyDown } from "./set-entry-keyboard";

const { RPE, RPEAMRAP, FREEFORM, LOAD } = PARAMETER_OPTIONS;

const AddSetRow = ({
  primary,
  selectedExercise,
  handleNewSetChange,
  isRiR,
  intensityMeasure,
  isAssistant,
  handleUnitChange,
  newSet,
  preferredLoadInput,
  setPreferredLoadInput,
  isNewSetValid,
  handleAddSet,
  handleParameterInputChange,
  activeUser,
  parametersForm,
  complexLength,
  // Opts in to the Add Exercise button flow: Enter runs the next logical step
  // and the Add set button follows the primary/disabled states. Without it the
  // row behaves as the Complexes tab expects (Enter adds the set, focus moves
  // to the footer, secondary +Set button).
  buttonFlow,
}) => {
  const { t } = useTranslation("coaching-portal");
  const commitSetFromKeyboard = (e) => {
    if (!buttonFlow) {
      if (!isNewSetValid) return;
      e.focusAfterCommit = "primary";
      handleAddSet(e);
      return;
    }
    const action = getEnterAction({
      rowValid: isNewSetValid,
      rowEmpty: buttonFlow.rowEmpty,
      exerciseHasSets: buttonFlow.exerciseHasSets,
      queueEnabled: buttonFlow.queueEnabled,
    });
    if (action === "addSet") {
      e.focusAfterCommit = "sets";
      handleAddSet(e);
    } else if (action === "addToWorkout") {
      buttonFlow.onAddToWorkout(e);
    }
  };
  const onSetFieldKeyDown = (e) =>
    handleSetEntryKeyDown(e, commitSetFromKeyboard);
  const addSetButton = buttonFlow ? (
    <button
      tabIndex={"0"}
      data-button-type={isNewSetValid ? "primary" : "secondary"}
      className="add-set-button"
      disabled={!isNewSetValid}
      onClick={(e) => handleAddSet(e)}
      type="button"
    >
      <SvgIcon src="/img/new-icons/add.svg" useCurrentColor />
      {t("addExerciseModal.addSet")}
    </button>
  ) : (
    <button
      tabIndex={"0"}
      data-button-type="secondary"
      className="mt-4"
      disabled={!isNewSetValid}
      onClick={(e) => handleAddSet(e)}
      type="button"
    >
      {t("addExerciseModal.addSetButton")}
    </button>
  );

  if (primary === "assistant" && selectedExercise?.parameters) {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: Enter commits the set from whichever field in the row is focused.
      <div className="add-set-wrapper" onKeyDown={onSetFieldKeyDown}>
        {selectedExercise.parameters.map((elem) => {
          return (
            <div
              key={`${elem}-add-set`}
              className={`column${
                elem === FREEFORM ? " free-form-col" : " relative"
              }`}
            >
              <label htmlFor={elem.toLowerCase()}>
                {[RPE, RPEAMRAP].includes(elem) ? intensityMeasure : elem}
                {elem === LOAD && preferredLoadInput === "drop"
                  ? ` ${t("addExerciseModal.modSuffix")}`
                  : ""}
              </label>
              {elem === LOAD && (
                <ModTooltip preferredLoadInput={preferredLoadInput} />
              )}
              <ParameterInput
                parameter={elem}
                value={parametersForm[mapParameter(elem)]}
                onChange={handleParameterInputChange}
                units={activeUser.units}
                tabIndex={"0"}
                id={`${elem}_id_parameter`}
                intensityMeasure={intensityMeasure}
                isRiR={isRiR}
                preferredLoadInput={preferredLoadInput}
                loadDrop={parametersForm.loadDrop}
              />
            </div>
          );
        })}

        {selectedExercise?.parameters?.includes(LOAD) && (
          <LoadToggler
            isAssistant
            preferredLoadInput={preferredLoadInput}
            setPreferredLoadInput={setPreferredLoadInput}
            tabIndex={-1}
          />
        )}

        <TimeSelector onChange={handleParameterInputChange} tabIndex={-1} />

        {addSetButton}
      </div>
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Enter commits the set from whichever field in the row is focused.
    <div className="add-set-wrapper" onKeyDown={onSetFieldKeyDown}>
      <div className="column">
        <label htmlFor="exerciseAddSets">{t("addExerciseModal.sets")}</label>
        <input
          tabIndex={"0"}
          id="exerciseAddSets"
          type="number"
          min={1}
          step="1"
          name="sets"
          onChange={(e) => handleNewSetChange(e)}
        />
      </div>

      {!complexLength ? (
        <div className="column">
          <label htmlFor="exerciseAddReps">{t("addExerciseModal.reps")}</label>
          <input
            tabIndex={"0"}
            id="exerciseAddReps"
            type="text"
            name="reps"
            onChange={(e) => handleNewSetChange(e)}
          />
        </div>
      ) : (
        <div className="column complex-reps">
          <div className="input-group">
            <label htmlFor="exerciseAddReps">
              {t("addExerciseModal.reps")}
            </label>
            <input
              tabIndex={"0"}
              id="exerciseAddReps"
              type="text"
              name="reps"
              onChange={(e) => handleNewSetChange(e)}
            />
          </div>
          <div className="input-group">
            <div className="input-divider" />
            <label htmlFor="exerciseAddRepsTwo">
              {t("addExerciseModal.plusReps")}
            </label>
            <input
              tabIndex={"0"}
              id="exerciseAddRepsTwo"
              type="text"
              name="repsTwo"
              onChange={(e) => handleNewSetChange(e)}
            />
          </div>
          {complexLength > 2 && (
            <div className="input-group">
              <div className="input-divider" />
              <label htmlFor="exerciseAddRepsThree">
                {t("addExerciseModal.plusReps")}
              </label>
              <input
                tabIndex={"0"}
                id="exerciseAddRepsThree"
                type="text"
                name="repsThree"
                onChange={(e) => handleNewSetChange(e)}
              />
            </div>
          )}
        </div>
      )}

      <div className="column">
        <label htmlFor="exerciseAddRpe">
          {isRiR ? t("addExerciseModal.rir") : t("addExerciseModal.rpeAmrap")}
        </label>
        <select
          tabIndex={"0"}
          id="exerciseAddRpe"
          name="rpe"
          defaultValue={""}
          onChange={(e) => handleNewSetChange(e)}
        >
          <option value="">{intensityMeasure}</option>
          {RPE_RANGE.map((i) => (
            <option key={i} value={i + 1}>
              {convertToRiR(i + 1, !isRiR)}
            </option>
          ))}
          <option value="AMRAP">AMRAP</option>
        </select>
      </div>

      <div className="column load relative">
        <label>
          {t("addExerciseModal.load")}
          {preferredLoadInput === "drop"
            ? ` ${t("addExerciseModal.modSuffix")}`
            : ""}
        </label>
        <ModTooltip preferredLoadInput={preferredLoadInput} />
        {isAssistant ? (
          <div className="Load-Selector">
            <div className={preferredLoadInput === "drop" ? "hidden" : ""}>
              <UnitInput
                tabIndex={preferredLoadInput === "drop" ? "-1" : "0"}
                metric="kg"
                imperial="lb"
                id="exerciseAddWeightLoad"
                className="text-input"
                name="load"
                placeholder="Weight"
                defaultValue={0}
                type="text"
                onProgramEditor={true}
                onInput={(e) => handleNewSetChange(e)}
                units={newSet.units["load"]}
                handleUnitChange={handleUnitChange}
                unitsTabIndex={-1}
                min={0}
              />
            </div>
            <div
              className={preferredLoadInput === "drop" ? "relative" : "hidden"}
            >
              <input
                tabIndex={preferredLoadInput === "drop" ? "0" : "-1"}
                type="text"
                name="loadDrop"
                id="exerciseAddLoadDrop"
                defaultValue={0}
                placeholder="%"
                onInput={(e) => handleNewSetChange(e)}
              />
              <span className="load-suffix">
                {t("addExerciseModal.percentModSuffix")}
              </span>
            </div>
          </div>
        ) : (
          <div className="Load-Selector-2">
            <div
              className={
                preferredLoadInput === "percent" ? "relative" : "hidden"
              }
            >
              <input
                tabIndex={preferredLoadInput === "percent" ? "0" : "-1"}
                type="text"
                min={0}
                id="exerciseAddLoad"
                name="percentRepMax"
                defaultValue={0}
                placeholder="%"
                onInput={(e) => handleNewSetChange(e)}
              />
              <span className="load-suffix">%</span>
            </div>
            <div className={preferredLoadInput === "weight" ? "" : "hidden"}>
              <UnitInput
                tabIndex={preferredLoadInput === "weight" ? "0" : "-1"}
                metric="kg"
                imperial="lb"
                name="load"
                placeholder="Weight"
                defaultValue={0}
                id="exerciseAddWeightLoad"
                type="text"
                onProgramEditor={true}
                onInput={(e) => handleNewSetChange(e)}
                units={newSet.units["load"]}
                handleUnitChange={handleUnitChange}
                unitsTabIndex={-1}
                min={0}
              />
            </div>
            <div
              className={preferredLoadInput === "drop" ? "relative" : "hidden"}
            >
              <input
                tabIndex={preferredLoadInput === "drop" ? "0" : "-1"}
                type="text"
                name="loadDrop"
                id="exerciseAddLoadDrop"
                defaultValue={0}
                placeholder="%"
                onInput={(e) => handleNewSetChange(e)}
              />
              <span className="load-suffix">
                {t("addExerciseModal.percentModSuffix")}
              </span>
            </div>
          </div>
        )}
      </div>

      <LoadToggler
        isAssistant={isAssistant}
        preferredLoadInput={preferredLoadInput}
        setPreferredLoadInput={setPreferredLoadInput}
        tabIndex={-1}
      />

      <TimeSelector onChange={handleNewSetChange} tabIndex={-1} />

      {addSetButton}
    </div>
  );
};

export default AddSetRow;
